import { motion } from 'framer-motion';
import {
  Presentation,
  Briefcase,
  Sparkles,
  ShieldCheck,
  Compass,
  Workflow
} from 'lucide-react';

type Feature = {
  icon: JSX.Element;
  title: string;
  description: string;
};

const trainingFeatures: Feature[] = [
  {
    icon: <Presentation className="h-6 w-6" />,
    title: "Sensibilisation et séminaires IA",
    description:
      "Acculturer largement vos équipes, du webinaire de 2 heures au séminaire de plusieurs centaines de personnes. Sans prérequis technique."
  },
  {
    icon: <Briefcase className="h-6 w-6" />,
    title: "Formations métier sur mesure",
    description:
      "Des ateliers construits sur vos cas d’usage réels, métier par métier : RH, marketing, commercial, finance, juridique, opérations."
  },
  {
    icon: <Sparkles className="h-6 w-6" />,
    title: "Maîtrise des outils IA",
    description:
      "ChatGPT, Copilot, Claude, Gemini : prompting, GPTs personnalisés et premiers agents, appliqués aux tâches quotidiennes de vos équipes."
  },
  {
    icon: <ShieldCheck className="h-6 w-6" />,
    title: "IA responsable et conformité",
    description:
      "Article 4 de l’AI Act, RGPD, charte d’usage interne : former vos collaborateurs à un usage sûr, encadré et documenté de l’IA."
  }
];

const consultingFeatures: Feature[] = [
  {
    icon: <Compass className="h-6 w-6" />,
    title: "Diagnostic et feuille de route IA",
    description:
      "Identification et priorisation de vos cas d’usage IA, estimation des gains attendus et plan de déploiement réaliste."
  },
  {
    icon: <Workflow className="h-6 w-6" />,
    title: "Agents IA et automatisation",
    description:
      "Conception d’agents et de workflows sur vos outils existants, du premier prototype à la mise en production."
  }
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const FeatureCard = ({ feature }: { feature: Feature }) => (
  <motion.div
    className="bg-gray-50 p-8 rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
    variants={item}
  >
    <div className="bg-primary/10 p-3 rounded-full w-fit mb-6">
      <div className="text-primary">{feature.icon}</div>
    </div>

    <h3 className="text-xl text-gray-900 font-semibold mb-3">{feature.title}</h3>

    <p className="text-gray-600">{feature.description}</p>
  </motion.div>
);

const Features = () => {
  return (
    <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 md:py-28">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="heading-lg mb-6">Formation IA et accompagnement</h2>

          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Notre métier, c’est former vos équipes à l’intelligence artificielle. Nous les accompagnons
            ensuite sur les premiers projets, pour que la formation se transforme en usages installés.
          </p>
        </div>

        {/* Bloc 1 : formation, le coeur de l'offre */}
        <div className="mb-6 flex items-center gap-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-primary">
            Former vos équipes
          </h3>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {trainingFeatures.map((feature) => (
            <FeatureCard key={feature.title} feature={feature} />
          ))}
        </motion.div>

        {/* Bloc 2 : accompagnement, en prolongement */}
        <div className="mb-6 flex items-center gap-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-primary">
            Accompagner vos premiers projets
          </h3>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {consultingFeatures.map((feature) => (
            <FeatureCard key={feature.title} feature={feature} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Features;